import { FC } from 'react';
import {
  NavigationPageItemDto,
  OneColumnGroupDto,
  PageContentError,
  TabDto,
  TabGroupDto,
  TwoColumnGroupDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  OneColumnGroup,
  Tab,
  TabGroup,
  TwoColumnGroup,
} from '@asset-metrix/portal-ui';

import i18n from '../../i18n/config';
import { isPageEditDummyWidgetId } from '../../utils/pageEditModeHelpers';
import { Table } from '../table/Table';
import { TableauChart } from '../tableauchart/TableauChart';
import { TextFromTable } from '../textFromTable/TextFromTable';
import { VegaChart } from '../vegachart/VegaChart';

import { PageContentError as PageContentErrorRenderer } from './PageContentError';
import { PageEditDummyWidget } from './PageEditDummyWidget';

function resolveContentChildKey(
  pageId: string,
  parentKey: string | undefined,
  content: { type: string; id?: string },
  index: number
): string {
  if (content.type === 'widget' && content.id != null) {
    return `${pageId}-${content.id}`;
  }
  return `${parentKey}-${index}`;
}

function WidgetContent({
  pageNode,
  pageId,
  instanceId,
}: Omit<PageContentRendererProps<WidgetDto>, 'Render' | 'child_key'>) {
  const entity = pageNode.entity;
  const widgetKey = `${pageId}-${pageNode.id}`;

  if (isPageEditDummyWidgetId(pageNode.id)) {
    return (
      <PageEditDummyWidget
        key={widgetKey}
        pageId={pageId}
        widgetId={pageNode.id}
        title={pageNode.title}
      />
    );
  }

  if (entity.type === 'table') {
    return (
      <Table
        key={widgetKey}
        pageId={pageId}
        instanceId={instanceId}
        {...pageNode}
        entity={entity}
      />
    );
  }
  if (entity.type === 'tableauChart') {
    return (
      <TableauChart
        key={widgetKey}
        pageId={pageId}
        {...pageNode}
        entity={entity}
        instanceId={instanceId}
      />
    );
  }
  if (entity.type === 'vegaChart') {
    return (
      <VegaChart
        key={widgetKey}
        {...pageNode}
        entity={entity}
        pageId={pageId}
        instanceId={instanceId}
      />
    );
  }
  if (entity.type === 'textFromTable') {
    return (
      <TextFromTable
        key={widgetKey}
        entity={entity}
        widgetId={pageNode.id}
        pageId={pageId}
        pageFilterInstanceId={instanceId}
      />
    );
  }

  throw new Error('Widget entity unknown');
}

function SingleColumGroupContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<OneColumnGroupDto>) {
  return (
    <OneColumnGroup key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => {
          const contentKey = resolveContentChildKey(
            pageId,
            child_key,
            content,
            index
          );
          return (
            <Render
              key={contentKey}
              child_key={contentKey}
              pageNode={content}
              Render={Render}
              pageId={pageId}
              instanceId={instanceId}
            />
          );
        })}
    </OneColumnGroup>
  );
}

function TwoColumGroupContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<TwoColumnGroupDto>) {
  return (
    <TwoColumnGroup key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => {
          const contentKey = resolveContentChildKey(
            pageId,
            child_key,
            content,
            index
          );
          return (
            <Render
              key={contentKey}
              child_key={contentKey}
              pageNode={content}
              Render={Render}
              pageId={pageId}
              instanceId={instanceId}
            />
          );
        })}
    </TwoColumnGroup>
  );
}

function TabGroupContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<TabGroupDto>) {
  return (
    <TabGroup entity={pageNode} key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => {
          const contentKey = resolveContentChildKey(
            pageId,
            child_key,
            content,
            index
          );
          return (
            <Render
              key={contentKey}
              child_key={contentKey}
              pageNode={content}
              Render={Render}
              pageId={pageId}
              instanceId={instanceId}
            />
          );
        })}
    </TabGroup>
  );
}

function TabContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<TabDto>) {
  return (
    <Tab entity={pageNode} key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => {
          const contentKey = resolveContentChildKey(
            pageId,
            child_key,
            content,
            index
          );
          return (
            <Render
              key={contentKey}
              child_key={contentKey}
              pageNode={content}
              Render={Render}
              pageId={pageId}
              instanceId={instanceId}
            />
          );
        })}
    </Tab>
  );
}

export type PageContentRendererProps<
  T =
    | PageContentError
    | WidgetDto
    | OneColumnGroupDto
    | TwoColumnGroupDto
    | TabGroupDto
    | TabDto,
> = {
  pageNode: T;
  Render: FC<PageContentRendererProps>;
  pageId: NavigationPageItemDto['pageId'];
  instanceId: string;
  child_key?: string;
};

export function PageContentRenderer({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps) {
  switch (pageNode.type) {
    case 'widget':
      return WidgetContent({ pageNode, pageId, instanceId });
    case 'singleColumnGroup':
      return SingleColumGroupContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'twoColumnGroup':
      return TwoColumGroupContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'tabGroup':
      return TabGroupContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'tab':
      return TabContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'error':
      return <PageContentErrorRenderer message={pageNode.message} />;
    default:
      return (
        <div style={{ color: 'red' }}>
          {i18n.t('app.page.unknownPageContentNodeError')}
        </div>
      );
  }
}
